"use client";

import { useLazyQuery } from "@apollo/client/react";
import { default as NextImage } from "next/image";
import Link from "next/link";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Blurhash } from "react-blurhash";
import { LuImage, LuLock, LuVideo } from "react-icons/lu";
import { useLocalStorage } from "usehooks-ts";

import { EmptyState } from "@/components/shared/empty-state";
import type { PostType } from "@/contexts/feed-context";
import { MediaPostCategoryFilter } from "@/contexts/feed-context";
import { usePostCacheContext } from "@/contexts/post-cache-context";
import { PostContextProvider, usePostContext } from "@/contexts/post-context";
import { UserContext } from "@/contexts/user-context";
import type { PageInfo, Post } from "@/graphql/codegen/graphql";
import { GetAccessGroupMemberEncryptedPrivateKeyDocument } from "@/graphql/codegen/graphql";
import { useImageMeta } from "@/hooks/use-image-meta";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { useGetPosts } from "@/hooks-api/use-get-posts";
import type { PostWithGatedContent } from "@/hooks-api/use-media-posts";
import { useMediaPosts } from "@/hooks-api/use-media-posts";
import { APP_ROUTES } from "@/utils/constants";
import { LocalStorageKeys } from "@/utils/local-storage-keys";
import { getSafeMediaDimensions } from "@/utils/media-dimensions";
import type { ImageMetadata, VideoMetadata } from "@/utils/media-utils";
import { getMediaMetaFromUrl } from "@/utils/post-utils";
import { getUserName } from "@/utils/usernames";

import { Spinner } from "../../shared/spinner";
import { FeedFilter } from "../feed-filter";
import { PostVideoSection } from "../post/post-video-section";

type CardProps = {
  data: PostWithGatedContent;
  mediaType: MediaTypeValue;
  url?: string;
};

type MediaGatedCardProps = {
  data: PostWithGatedContent;
};

const FILTERS = [
  {
    label: "Images",
    postCategory: MediaPostCategoryFilter.ImageUrls,
    icon: LuImage,
  },
  {
    label: "Videos",
    postCategory: MediaPostCategoryFilter.VideoUrls,
    icon: LuVideo,
  },
];

export enum MEDIA_TYPE {
  ImageUrls = "imageUrls",
  VideoUrls = "videoUrls",
  SubscriptionEncryptedImageURLs = "subscriptionEncryptedImageURLs",
  SubscriptionEncryptedVideoURLs = "subscriptionEncryptedVideoURLs",
}

export type MediaTypeValue = (typeof MEDIA_TYPE)[keyof typeof MEDIA_TYPE];

const PostSpinner = () => {
  return (
    <div className="flex h-24 items-center justify-center">
      <Spinner size={38} />
    </div>
  );
};

const getMediaCardStyles = ({
  width,
  height,
}: {
  width: number;
  height: number;
}) => {
  const dimensions = getSafeMediaDimensions(
    { width, height },
    { width: 1600, height: 900 },
  );
  const cardWidth = Math.max((dimensions.width * 150) / dimensions.height, 120);

  return {
    flexGrow: cardWidth,
    width: cardWidth,
    aspectRatio: `${dimensions.width} / ${dimensions.height}`,
  };
};

const MediaGatedCard = ({
  data: { gatedContent, postHash },
}: MediaGatedCardProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [blurHashDimensions, setBlurHashDimensions] = useState<{
    height: number;
    width: number;
  } | null>(null);

  useEffect(() => {
    const height = containerRef.current?.clientHeight;
    const width = containerRef.current?.clientWidth;

    if (height && width) {
      setBlurHashDimensions({ height, width });
    }
  }, []);

  return (
    <div
      className="m-1 flex min-h-64 items-center justify-center"
      ref={containerRef}
      style={getMediaCardStyles({
        width: blurHashDimensions?.width ?? 1600,
        height: blurHashDimensions?.height ?? 900,
      })}
    >
      <Link
        className="
          group relative flex break-inside-avoid items-center justify-center
          overflow-hidden
        "
        href={`${APP_ROUTES.post}/${postHash}`}
        prefetch={false}
      >
        <LuLock
          size={20}
          className="absolute z-20 text-base text-primary-foreground"
        />
        <Blurhash
          hash={gatedContent?.SubscriptionBlurHash ?? ""}
          height={blurHashDimensions?.height ?? 0}
          width={blurHashDimensions?.width ?? 0}
          className="w-full"
        />
      </Link>
    </div>
  );
};

const MediaCard = ({
  data: { poster, postHash },
  url,
  mediaType,
}: CardProps) => {
  const { mediaMeta } = usePostContext();
  const { getPostImageSize, postsImageSizeMap } = usePostCacheContext();
  const metaData = getMediaMetaFromUrl(url as string, mediaMeta) as
    | ImageMetadata
    | VideoMetadata;

  let { height, width } = getSafeMediaDimensions(
    metaData,
    getPostImageSize(url as string),
  );

  if (
    mediaType === MEDIA_TYPE.VideoUrls ||
    mediaType === MEDIA_TYPE.SubscriptionEncryptedVideoURLs
  ) {
    width = 1600;
    height = 900;
  }

  const {
    flexGrow,
    aspectRatio,
    width: cardWidth,
  } = getMediaCardStyles({
    width,
    height,
  });

  useEffect(() => {
    if (
      url &&
      (mediaType === MEDIA_TYPE.ImageUrls ||
        mediaType === MEDIA_TYPE.SubscriptionEncryptedImageURLs)
    ) {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        postsImageSizeMap.set(url, {
          width: img.width,
          height: img.height,
        });
      };
    }
  }, [url, mediaType, postsImageSizeMap, height, width]);

  if (!Number.isFinite(width) || !Number.isFinite(height)) {
    return (
      <div className="m-1 flex items-center justify-center">
        <Link
          className="
            group relative flex size-full break-inside-avoid items-center
            justify-center overflow-hidden
          "
          href={`${APP_ROUTES.post}/${postHash}`}
          prefetch={false}
        >
          <div
            style={getMediaCardStyles({
              width: 70,
              height: 210,
            })}
            className="flex size-full items-center justify-center bg-card p-2"
          >
            <p className="flex items-center text-center text-sm text-muted">
              Content not found
            </p>
          </div>
        </Link>
      </div>
    );
  }

  return (
    <div
      className="m-px flex items-center justify-center"
      style={{ flexGrow, aspectRatio, width: cardWidth }}
    >
      <Link
        className="
          group relative flex size-full break-inside-avoid items-center
          justify-center overflow-hidden
        "
        href={`${APP_ROUTES.post}/${postHash}`}
        prefetch={false}
      >
        {(mediaType === MEDIA_TYPE.ImageUrls ||
          mediaType === MEDIA_TYPE.SubscriptionEncryptedImageURLs) && (
          <div className="relative size-full">
            <NextImage
              src={url ?? ""}
              className="
                min-h-full w-full object-cover transition-transform duration-200
                ease-in-out
                group-hover:scale-110
              "
              width={width}
              height={height}
              loading="lazy"
              quality={90}
              alt={`Image from ${poster ? getUserName({ account: poster }) + "'s" : "Anonymous"} post`}
            />
          </div>
        )}
        {(mediaType === MEDIA_TYPE.VideoUrls ||
          mediaType === MEDIA_TYPE.SubscriptionEncryptedVideoURLs) && (
          <PostVideoSection
            videoUrls={[url ?? ""]}
            className="
              min-h-full w-full rounded-none border border-gray-500 object-cover
            "
            wrapperClassName="size-full"
            postHash={postHash}
          />
        )}
      </Link>
    </div>
  );
};

const DEFAULT_MEDIA_FILTERS = {
  [MediaPostCategoryFilter.ImageUrls]: true,
  [MediaPostCategoryFilter.VideoUrls]: true,
};

export const FeedMedia = ({ userPublicKey }: { userPublicKey: string }) => {
  const { getPosts } = useGetPosts();
  const [postsInfo, setPostsInfo] = useState<PageInfo | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [allImagesAreLoaded, setAllImagesAreLoaded] = useState(true);
  const { getImageMeta } = useImageMeta();
  const [loadingData, setLoadingData] = useState(false);
  const [getEncryptedPrivateKey] = useLazyQuery(
    GetAccessGroupMemberEncryptedPrivateKeyDocument,
    {
      fetchPolicy: "network-only",
    },
  );
  const {
    user,
    loading: userLoading,
    currentUserPublicKey,
  } = useContext(UserContext);

  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.5,
  });
  const [mediaPosts, setMediaPosts] = useState<PostType[]>([]);
  const [mediaPostCategoryFilter, setMediaPostCategoryFilter] = useLocalStorage<
    Record<MediaPostCategoryFilter, boolean>
  >(LocalStorageKeys.filtersProfileMedia, DEFAULT_MEDIA_FILTERS);

  const fetchMediaPosts = useMediaPosts({
    mediaPostCategoryFilter: mediaPostCategoryFilter || DEFAULT_MEDIA_FILTERS,
    getPosts,
    userPublicKey,
    setPostsInfo,
  });

  const waitForAllImages = useCallback(
    async (newMediaPosts: Post[]) => {
      setAllImagesAreLoaded(false);
      const imageUrls = newMediaPosts
        ?.flatMap((post) => {
          return post.imageUrls || [""];
        })
        .filter(Boolean) as string[];

      if (imageUrls?.length > 0) {
        await getImageMeta(imageUrls);
      }

      setAllImagesAreLoaded(true);
    },
    [getImageMeta],
  );

  useEffect(() => {
    const loadMoreMediaPosts = async () => {
      const hasNextPage = postsInfo?.hasNextPage;

      if (
        isIntersecting &&
        !initialLoading &&
        hasNextPage &&
        !loadingData &&
        allImagesAreLoaded
      ) {
        setLoadingData(true);
        const newMediaPosts = await fetchMediaPosts?.(
          user,
          getEncryptedPrivateKey,
          false,
          hasNextPage,
          mediaPosts?.length ?? 0,
        );

        if (newMediaPosts) {
          await waitForAllImages(newMediaPosts);
        }

        if (newMediaPosts) {
          setMediaPosts((mediaPosts) => [...mediaPosts, ...newMediaPosts]);
        }

        setLoadingData(false);
      }
    };

    loadMoreMediaPosts();
  }, [
    fetchMediaPosts,
    getEncryptedPrivateKey,
    initialLoading,
    isIntersecting,
    mediaPosts,
    waitForAllImages,
    user,
    loadingData,
    allImagesAreLoaded,
    postsInfo?.hasNextPage,
  ]);

  useEffect(() => {
    const initialLoad = async () => {
      if (!userPublicKey) return;
      const newMediaPosts = await fetchMediaPosts(
        user,
        getEncryptedPrivateKey,
        true,
      );
      setMediaPosts(newMediaPosts as PostType[]);
      setInitialLoading(false);
    };

    initialLoad();
  }, [
    fetchMediaPosts,
    getEncryptedPrivateKey,
    user,
    currentUserPublicKey,
    userPublicKey,
  ]);

  const renderCards = useCallback((post: PostWithGatedContent) => {
    const mediaTypes = [
      MEDIA_TYPE.ImageUrls,
      MEDIA_TYPE.SubscriptionEncryptedImageURLs,
      MEDIA_TYPE.SubscriptionEncryptedVideoURLs,
      MEDIA_TYPE.VideoUrls,
    ];

    return mediaTypes.flatMap((mediaType) => {
      const isGatedContent =
        mediaType === MEDIA_TYPE.SubscriptionEncryptedImageURLs ||
        mediaType === MEDIA_TYPE.SubscriptionEncryptedVideoURLs;
      const camelCaseMethodName =
        mediaType.charAt(0).toUpperCase() + mediaType.slice(1);

      if (
        isGatedContent &&
        post.extraData?.[camelCaseMethodName] &&
        !post?.[mediaType]
      ) {
        if (!post.viewerIsSubscribed && post.gatedContent) {
          return;
          <PostContextProvider post={post} key={`${post.postHash}gated`}>
            <MediaGatedCard data={post} />
          </PostContextProvider>;
        }
        return null;
      }

      return post?.[mediaType]?.map((url) => {
        if (!url || (isGatedContent && !post.viewerIsSubscribed)) return null;

        return (
          <PostContextProvider post={post} key={url}>
            <MediaCard data={post} mediaType={mediaType} url={url} />
          </PostContextProvider>
        );
      });
    });
  }, []);

  return (
    <div
      className="
        relative my-0 w-full bg-background
        sm:min-h-safe-screen
      "
    >
      <div
        key="feed-filter"
        className="
          flex justify-between border-b border-border-light p-0 text-xs
        "
      >
        <FeedFilter
          items={FILTERS}
          filter={mediaPostCategoryFilter || DEFAULT_MEDIA_FILTERS}
          setFilter={setMediaPostCategoryFilter}
        />
      </div>

      {(userLoading || initialLoading) && <PostSpinner key="user-spinner" />}

      {!userLoading && !initialLoading && mediaPostCategoryFilter && (
        <section
          className="
            flex flex-wrap
            after:grow-999999999
          "
        >
          {(mediaPosts as PostWithGatedContent[]).map(renderCards)}
          {mediaPosts.length === 0 && (
            <div className="w-full px-4 py-10 text-center">
              {mediaPostCategoryFilter[MediaPostCategoryFilter.ImageUrls] &&
                mediaPostCategoryFilter[MediaPostCategoryFilter.VideoUrls] && (
                  <EmptyState
                    image="/assets/empty-32.png"
                    title="No images or videos found."
                    description="Check back later for more content."
                  />
                )}
              {mediaPostCategoryFilter[MediaPostCategoryFilter.ImageUrls] &&
                !mediaPostCategoryFilter[MediaPostCategoryFilter.VideoUrls] && (
                  <EmptyState
                    image="/assets/empty-32.png"
                    title="No images found."
                    description="Check back later for more content."
                  />
                )}
              {!mediaPostCategoryFilter[MediaPostCategoryFilter.ImageUrls] &&
                mediaPostCategoryFilter[MediaPostCategoryFilter.VideoUrls] && (
                  <EmptyState
                    image="/assets/empty-32.png"
                    title="No videos found."
                    description="Check back later for more content."
                  />
                )}
              {!mediaPostCategoryFilter[MediaPostCategoryFilter.ImageUrls] &&
                !mediaPostCategoryFilter[MediaPostCategoryFilter.VideoUrls] && (
                  <EmptyState
                    image="/assets/empty-32.png"
                    title="Please select at least one content type."
                    description="Select at least one content type to see the posts."
                  />
                )}
            </div>
          )}
        </section>
      )}

      {postsInfo?.hasNextPage && allImagesAreLoaded && (
        <>
          <div ref={ref} key="feed-loader" />
          <PostSpinner key="feed-load-more-spinner" />
        </>
      )}
    </div>
  );
};
